import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Grid,
  Select,
  Button,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

const THATransactionDetails = () => {
  const [simData, setSimData] = useState<any[]>([]);
  const [countryListN, setCountryListN] = useState<any[]>([]);
  const [subCategoryList, setSubCategoryList] = useState<any[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [filteredSubCategories, setFilteredSubCategories] = useState<any[]>([]);

  const [country, setCountry] = useState<any>("0");
  const [product, setProduct] = useState<any>("0");
  const [operator, setOperator] = useState<any>(0);
  const [rowData, setRowData] = useState<any[]>([]);
  const [fullData, setFullData] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const todayDate = dayjs().format("YYYY-MM-DD");
  // New Filter States
  const [endDate, setEndDate] = useState<any>(todayDate);
  const [startDate, setStartDate] = useState<any>(todayDate);
  const [vendorStatus, setVendorStatus] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethodStatus, setPaymentMethodStatus] = useState("");
  const [originationCountry, setOriginationCountry] = useState<any>("0");

  const [paginationModel, setPaginationModel] = useState<any>({
    page: 1,
    pageSize: 50,
  });

  const today = dayjs();
  const isFirstRender = useRef(true);

  const loadCountries = async () => {
    await fetch("/data/thacountry.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json: any) => {
        setCountryListN(json.thacountries);
      });
  };
  const loadSubCategory = async () => {
    await fetch("/data/subCategory.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json: any) => {
        setSubCategoryList(json.subCategory);
      });
  };

  useEffect(() => {
    if (product === "gift_cards" || product === "mobile") {
      const filtered = subCategoryList?.filter(
        (item: any) => item.categoryAliasName === product
      );
      setFilteredSubCategories(filtered);
    } else {
      setFilteredSubCategories([]);
      setSelectedSubCategory("");
    }
  }, [product]);

  useEffect(() => {
    loadCountries();
    getOperator();
    loadSubCategory();
  }, []);

  const getProductData = () => {
    setPaginationModel((prev: any) => ({
      ...prev,
      page: 1,
    }));
  };

  const filteredSortedOperators = useMemo(() => {
    setOperator("");
    if (!fullData.length) return [];

    const filteredData = fullData?.filter((item: any) => {
      return country && country !== "0"
        ? item.country.countryIsoCode === country.isoCode2
        : true;
    });

    const operators = filteredData?.map((item: any) => ({
      operator: item.operator,
      country: item.country,
    }));

    return operators;
  }, [country, fullData]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setRowData(filteredSortedOperators);
    }, 10);

    return () => clearTimeout(timeout);
  }, [filteredSortedOperators]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    getSimData();
  }, [paginationModel]);

  const getOperator = async () => {
    toast.dismiss();
    try {
      const response = await axiosInterceptorInstance.post(
        `/FusionHubCrm/GetOperators`,
        {
          page: 1,
          recordsPerPage: 10000,
        }
      );

      const responseData = response?.data.payload || [];
      setFullData(responseData);
    } catch (error) {
      console.error(error);
    }
  };

  const getSimData = async () => {
    toast.dismiss();
    setIsLoading(true);
    setSimData([]);

    await axiosInterceptorInstance
      .post(`/ThaCrm/ThaTransactionDetails`, {
        PageNumber: paginationModel.page,
        PageSize: paginationModel.pageSize,
        filters: {
          ...(country !== undefined && { Destination: country.isoCode2 }),
          ...(operator?.operator?.operatorShortCode && {
            operatorShortCode: operator.operator.operatorShortCode,
          }),
          ...(product !== "0" && { ProductType: product }),
          ...(selectedSubCategory && {
            ProductSubCategory: selectedSubCategory,
          }),
          ...(originationCountry !== undefined && {
            Origniation: originationCountry.isoCode2,
          }),
          ...(paymentMethod?.trim() && { PaymentMethod: paymentMethod.trim() }),
          ...(vendorStatus?.trim() && { VendorStatus: vendorStatus.trim() }),
          ...(paymentMethodStatus?.trim() && {
            PaymentMethodStatus: paymentMethodStatus.trim(),
          }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      })
      .then((response: any) => {
        const responseData = JSON.parse(response.data.body);
        const totalRecords = responseData?.TotalRecords;
        if (responseData.length == 0 && totalRecords > 0) {
          setPaginationModel((prev: any) => ({
            ...prev,
            page: paginationModel.page - 1,
          }));
        }
        if (responseData?.Data?.length == 0) {
          toast.info("No record found");
        }
        setSimData(responseData?.Data);
        setTotalRows(totalRecords);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "ProductTYpe",
      headerName: "ProductType",
      hideable: false,
      flex: 1,
      minWidth: 120,
    },
    {
      field: "ProductSubCategory",
      headerName: "ProductSubCategory",
      hideable: false,
      flex: 1,
      minWidth: 120,
    },
    {
      field: "ProductName",
      headerName: "ProductName",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Currency",
      headerName: "Currency",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "ProductPrice",
      headerName: "ProductPrice",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "ServiceFee",
      headerName: "ServiceFee",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Origination",
      headerName: "Origination",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Destination",
      headerName: "Destination",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "OperatorName",
      headerName: "OperatorName",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "OrderInitiated_Date",
      headerName: "OrderDate",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Vendor_Response_Date",
      headerName: "Vendor ResponseDate",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "Vendor_Status",
      headerName: "VendorStatus",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "PaymentMethod",
      headerName: "PaymentMethod",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "PaymentStatus",
      headerName: "PaymentStatus",
      flex: 1,
      minWidth: 120,
    },
  ];

  const defaultVisibility: any = {
    ProductTYpe: true,
    ProductSubCategory: true,
    ProductName: true,
    Currency: true,
    ProductPrice: false,
    ServiceFee: false,
    Origination: false,
    Destination: false,
    OperatorName: false,
    OrderInitiated_Date: false,
    Vendor_Response_Date: false,
    Vendor_Status: false,
    PaymentMethod: false,
    PaymentStatus: false,
  };

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<any>(defaultVisibility);

  function CustomToolbar() {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          padding: 8,
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">Transaction Details</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <fieldset>
                <legend>Basic Filters:</legend>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{ background: "#fff", padding: "0 5px" }}
                      >
                        Destination Countries
                      </InputLabel>
                      <Select
                        className="formControl"
                        autoWidth
                        value={country?.isoCode2 || 0}
                        onChange={(e: any) => {
                          const country = countryListN?.find(
                            (item) => item.isoCode2 === e.target.value
                          );
                          setCountry(country);
                        }}
                      >
                        <MenuItem value="0">Select Country</MenuItem>
                        {countryListN?.map((item, index) => (
                          <MenuItem key={index} value={item?.isoCode2}>
                            {item?.countryName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={3}>
                    <FormControl style={{ width: "100%" }}>
                      <InputLabel
                        style={{ background: "#fff", padding: "0 5px" }}
                      >
                        Origination Countries
                      </InputLabel>
                      <Select
                        className="formControl"
                        autoWidth
                        value={originationCountry?.isoCode2 || 0}
                        onChange={(e: any) => {
                          const country = countryListN?.find(
                            (item) => item.isoCode2 === e.target.value
                          );
                          setOriginationCountry(country);
                        }}
                      >
                        <MenuItem value="0">Select Country</MenuItem>
                        {countryListN?.map((item, index) => (
                          <MenuItem key={index} value={item?.isoCode2}>
                            {item?.countryName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={2.5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const formatted = newValue.format("YYYY-MM-DD");
                            setStartDate(formatted);
                            if (endDate && formatted > endDate) {
                              setEndDate(formatted);
                            }
                          } else {
                            setStartDate("");
                          }
                        }}
                        maxDate={endDate ? dayjs(endDate) : today}
                        disableFuture
                        slotProps={{
                          actionBar: {
                            actions: ["clear"],
                          },
                          textField: {
                            fullWidth: true,
                          },
                          field: {
                            readOnly: true,
                            onKeyDown: (e: React.KeyboardEvent) =>
                              e.preventDefault(),
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={2.5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const formatted = newValue.format("YYYY-MM-DD");
                            setEndDate(formatted);
                          } else {
                            setEndDate("");
                          }
                        }}
                        minDate={startDate ? dayjs(startDate) : undefined}
                        maxDate={today}
                        disableFuture
                        slotProps={{
                          actionBar: {
                            actions: ["clear"],
                          },
                          textField: {
                            fullWidth: true,
                          },
                          field: {
                            readOnly: true,
                            onKeyDown: (e: React.KeyboardEvent) =>
                              e.preventDefault(),
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={1} mt={4}>
                    <span
                      style={{ cursor: "pointer", opacity: "0.6" }}
                      onClick={() => setShowMoreFilters(!showMoreFilters)}
                    >
                      {showMoreFilters ? "-Hide Filters" : "+More Filters"}
                    </span>
                  </Grid>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    className="dButton dButton2"
                    onClick={() => {
                      getProductData();
                    }}
                    disabled={isLoading}
                    sx={{
                      margin: "0 auto  !important",
                      display: "block",
                      width: "280px !important",
                    }}
                  >
                    {isLoading ? <CircularProgress size={30} /> : "Submit"}
                  </Button>
                </Grid>
              </fieldset>

              {showMoreFilters && (
                <fieldset style={{ marginTop: 20 }}>
                  <legend>More Filters:</legend>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel
                          style={{ background: "#fff", padding: "0 5px" }}
                        >
                          Product Type
                        </InputLabel>
                        <Select
                          className="formControl"
                          autoWidth
                          value={product}
                          onChange={(e: any) => setProduct(e.target.value)}
                        >
                          <MenuItem value="0">Choose Type</MenuItem>
                          <MenuItem value="gift_cards"> Gift Card </MenuItem>
                          <MenuItem value="mobile">Mobile</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {product !== "0" && filteredSubCategories.length > 0 && (
                      <Grid item xs={4}>
                        <FormControl style={{ width: "100%" }}>
                          <InputLabel
                            style={{ background: "#fff", padding: "0 5px" }}
                          >
                            Sub Category
                          </InputLabel>
                          <Select
                            className="formControl"
                            autoWidth
                            value={selectedSubCategory}
                            onChange={(e: any) =>
                              setSelectedSubCategory(e.target.value)
                            }
                          >
                            <MenuItem value="">Choose Sub Category</MenuItem>
                            {filteredSubCategories?.map((sub, index) => (
                              <MenuItem
                                key={index}
                                value={sub.subCategoryShortCode}
                              >
                                {sub.subCategoryName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    <Grid item xs={4}>
                      <FormControl style={{ width: "100%" }}>
                        <InputLabel
                          style={{ background: "#fff", padding: "0 5px" }}
                        >
                          Operator
                        </InputLabel>
                        <Select
                          className="formControl"
                          autoWidth
                          value={operator?.operator?.operatorId || "0"}
                          onChange={(e: any) => {
                            const operator = rowData?.find(
                              (item: any) =>
                                item.operator?.operatorId === e.target.value
                            );
                            setOperator(operator);
                          }}
                        >
                          <MenuItem value="0">Choose Type</MenuItem>
                          {rowData.length > 0 ? (
                            rowData?.map((item: any, index: any) => (
                              <MenuItem
                                key={index}
                                value={item.operator?.operatorId}
                              >
                                {item.country?.countryIsoCode}-
                                {item.operator?.operatorName}
                              </MenuItem>
                            ))
                          ) : (
                            <div
                              className="circular-loader"
                              style={{ marginLeft: "0" }}
                            >
                              <CircularProgress size={40} />
                            </div>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Payment Method"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        inputProps={{ maxLength: 20 }}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Payment Status"
                        value={paymentMethodStatus}
                        onChange={(e) => setPaymentMethodStatus(e.target.value)}
                        inputProps={{ maxLength: 20 }}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Vendor Status"
                        value={vendorStatus}
                        onChange={(e) => setVendorStatus(e.target.value)}
                        inputProps={{ maxLength: 20 }}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              )}
            </Grid>
          </Grid>

          {simData?.length > 0 && (
            <div style={{ height: 550, width: "100%", marginTop: 16 }}>
              <DataGrid
                rows={
                  simData?.map((row: any, index: number) => ({
                    id: index + 1,
                    ...row,
                  })) || []
                }
                columns={columns}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                pagination
                rowCount={totalRows}
                paginationMode="server"
                pageSize={paginationModel.pageSize}
                rowsPerPageOptions={[10, 25, 50]}
                onPageSizeChange={(newPageSize) =>
                  setPaginationModel((prev: any) => ({
                    ...prev,
                    pageSize: newPageSize,
                  }))
                }
                page={paginationModel.page - 1}
                onPageChange={(newPage) => {
                  setPaginationModel((prev: any) => ({
                    ...prev,
                    page: newPage + 1,
                  }));
                }}
                disableColumnMenu
                components={{
                  Toolbar: CustomToolbar,
                }}
                getRowHeight={() => "auto"}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal !important", // allow wrapping
                    fontSize: "0.7rem",
                    wordBreak: "break-word !important",
                    padding: "5px 0px 5px 25px",
                  },
                }}
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default THATransactionDetails;
